import React, { useMemo } from 'react';
import {
  Box, Chip, Divider, Drawer, IconButton, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import RemoveIcon from '@mui/icons-material/Remove';
import { IHotspotAnalysis, IHotspotItem } from '../../../shared/services/analytics/hotspots.model';
import { getMixedColorPalette } from '../../../shared/utils/colors-utils';
import { GraphBarChart } from './graphs/graph-bar-chart';
import { GraphLineChart } from './graphs/graph-line-chart';

const fmtTco2e = new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtPct = (v: number | null | undefined) =>
  v === null || v === undefined || Number.isNaN(v) ? '–' : `${v.toFixed(1)}%`;

// Which card was clicked. Each maps to a self-contained explanatory panel.
export type HotspotMetric = 'total' | 'topShare' | 'drivers' | 'biggestMover';

// Pluralise a dimension label ('category' -> 'categories', 'supplier' -> 'suppliers').
const pluralise = (w: string) => {
  if (/[^aeiou]y$/i.test(w)) return w.replace(/y$/i, 'ies');
  if (/(s|x|z|ch|sh)$/i.test(w)) return `${w}es`;
  return `${w}s`;
};

// How many rows to show before summarising the tail (honest totals, not silent truncation).
const MAX_ROWS = 15;

const Stat: React.FC<{ label: string; value: string; color?: string }> = ({ label, value, color }) => (
  <Box>
    <Typography variant='h6' sx={{ color: color || 'text.primary' }}>{value}</Typography>
    <Typography variant='caption' color='text.secondary'>{label}</Typography>
  </Box>
);

// Signed Δ inline, matching the drawer / AnalyticMetric colour language.
const DeltaInline: React.FC<{ deltaAbs: number | null; deltaPct?: number | null; isNew?: boolean }> = ({ deltaAbs, deltaPct, isNew }) => {
  if (isNew) return <Chip size='small' label='New' color='warning' variant='outlined' />;
  if (deltaAbs === null || deltaAbs === undefined) return <Typography variant='body2' color='text.secondary'>–</Typography>;
  const isDecrease = deltaAbs < 0;
  const isNoChange = Math.abs(deltaAbs) < 0.005;
  const color = isNoChange ? 'text.secondary' : isDecrease ? 'success.main' : 'error.main';
  const Icon = isNoChange ? RemoveIcon : isDecrease ? ArrowDownwardIcon : ArrowUpwardIcon;
  return (
    <Stack direction='row' alignItems='center' spacing={0.25} justifyContent='flex-end'>
      <Icon sx={{ fontSize: '0.85rem', color }} />
      <Typography variant='body2' sx={{ color, fontVariantNumeric: 'tabular-nums' }}>
        {fmtTco2e.format(Math.abs(deltaAbs))}{deltaPct !== null && deltaPct !== undefined ? ` (${fmtPct(Math.abs(deltaPct))})` : ''}
      </Typography>
    </Stack>
  );
};

const Lead: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant='body2' color='text.secondary' sx={{ mb: 2, lineHeight: 1.6 }}>{children}</Typography>
);

const METRIC_TITLE: Record<HotspotMetric, string> = {
  total: 'Total Footprint',
  topShare: 'Top Hotspot Share',
  drivers: 'Drivers of 80%',
  biggestMover: 'Biggest Mover',
};

export const HotspotMetricDrawer: React.FC<{
  open: boolean;
  onClose: () => void;
  metric: HotspotMetric | null;
  data?: IHotspotAnalysis;
  dimensionKey: string;           // active 'Analyse hotspots by' dimension
  dimensionLabel: string;         // resolved, singular (e.g. 'Category', 'Supplier')
  defaultDimensionLabel: string;  // label of the dimension Biggest Mover is drawn from
  prevTotal?: number;
  comparisonLabel?: string;       // e.g. 'vs 2023/24'
}> = ({ open, onClose, metric, data, dimensionKey, dimensionLabel, defaultDimensionLabel, prevTotal, comparisonLabel }) => {
  const theme = useTheme();
  const colors = useMemo(() => getMixedColorPalette(theme.palette.primary.main), [theme.palette.primary.main]);
  const dimLower = dimensionLabel.toLowerCase();
  const dimPlural = pluralise(dimLower);

  const activeDimension = data?.dimensions?.[dimensionKey];
  const items = activeDimension?.items ?? [];
  const totals = data?.totals;
  const priorLabel = comparisonLabel || (data?.comparison ? `vs ${data.comparison.friendlyName}` : 'vs prior period');

  const renderBody = () => {
    if (!data || !metric) return null;

    if (metric === 'total') {
      const total = totals?.total ?? 0;
      const scopeRows = [
        { label: 'Scope 1', value: totals?.scope1 ?? 0 },
        { label: 'Scope 2', value: totals?.scope2 ?? 0 },
        { label: 'Scope 3', value: totals?.scope3 ?? 0 },
      ].filter((r) => r.value > 0);
      const trajectory = (data.reduction?.trajectory ?? []).filter((t) => t.value !== null);
      const delta = prevTotal !== undefined ? total - prevTotal : null;
      return (
        <Stack spacing={2}>
          <Paper variant='outlined' sx={{ p: 2.5 }}>
            <Stack direction='row' flexWrap='wrap' useFlexGap sx={{ gap: 3 }}>
              <Stat label='Total emissions' value={`${fmtTco2e.format(total)} tCO₂e`} />
              <Box>
                <DeltaInline deltaAbs={delta} />
                <Typography variant='caption' color='text.secondary'>{priorLabel}</Typography>
              </Box>
              {(totals?.perEmployee ?? 0) > 0 && (
                <Stat label='per employee' value={`${fmtTco2e.format(totals?.perEmployee ?? 0)} tCO₂e`} />
              )}
            </Stack>
          </Paper>
          <Lead>
            Your organisation&apos;s total gross greenhouse gas emissions for this report period, across all
            scopes and locations. Everything else on this page is a slice of this number.
          </Lead>
          {scopeRows.length > 0 && (
            <GraphBarChart
              title='By scope'
              tooltip='How the total splits across GHG Protocol scopes 1, 2 and 3.'
              dataset={scopeRows}
              series={[{ dataKey: 'value', label: 'tCO₂e' }]}
              dataKey='label'
              layout='horizontal'
              colors={colors}
              chartHeight={200}
              horizontalLabelGutter={90}
            />
          )}
          {((totals?.ukTotal ?? 0) > 0 || (totals?.nonUkTotal ?? 0) > 0) && (
            <Paper variant='outlined' sx={{ p: 2.5 }}>
              <Typography variant='subtitle2' sx={{ mb: 1.5 }}>By geography</Typography>
              <Stack direction='row' flexWrap='wrap' useFlexGap sx={{ gap: 3 }}>
                <Stat label='UK' value={`${fmtTco2e.format(totals?.ukTotal ?? 0)} tCO₂e`} />
                <Stat label='Non-UK' value={`${fmtTco2e.format(totals?.nonUkTotal ?? 0)} tCO₂e`} />
              </Stack>
            </Paper>
          )}
          {trajectory.length > 1 && (
            <GraphLineChart
              title='Footprint trajectory'
              tooltip='Total emissions by reporting year, showing the overall direction of travel.'
              dataset={trajectory.map((t) => ({ year: t.year, value: t.value ?? 0 }))}
              series={[{ dataKey: 'value', label: 'Total tCO₂e' }]}
              dataKey='year'
              colors={colors}
            />
          )}
        </Stack>
      );
    }

    if (metric === 'topShare') {
      const top = items[0];
      const share = activeDimension?.topShare ?? 0;
      const topFive = items.slice(0, 5);
      return (
        <Stack spacing={2}>
          <Paper variant='outlined' sx={{ p: 2.5 }}>
            <Stack direction='row' flexWrap='wrap' useFlexGap sx={{ gap: 3 }}>
              <Stat label={`share of ${dimLower} emissions`} value={fmtPct(share)} />
              {top && <Stat label='largest contributor' value={top.label} />}
            </Stack>
          </Paper>
          <Lead>
            {top ? (
              <>
                <b>{top.label}</b> alone accounts for {fmtPct(share)} of your {dimLower} emissions
                ({fmtTco2e.format(top.value)} tCO₂e). A high share means your footprint is concentrated in one
                place, so effort there has the most leverage. A low share means it is spread across many {dimPlural}.
              </>
            ) : (
              <>The largest single {dimLower} accounts for {fmtPct(share)} of emissions.</>
            )}
          </Lead>
          {topFive.length > 0 && (
            <Paper variant='outlined' sx={{ p: 2.5 }}>
              <Typography variant='subtitle2' sx={{ mb: 1 }}>Top {topFive.length} by share</Typography>
              <TableContainer>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, pl: 0 }}>{dimensionLabel}</TableCell>
                      <TableCell align='right' sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>tCO₂e</TableCell>
                      <TableCell align='right' sx={{ fontWeight: 600 }}>Share</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topFive.map((it) => (
                      <TableRow key={`${it.rank}-${it.label}`}>
                        <TableCell sx={{ pl: 0 }}>{it.label}</TableCell>
                        <TableCell align='right' sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtTco2e.format(it.value)}</TableCell>
                        <TableCell align='right' sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtPct(it.sharePct)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Stack>
      );
    }

    if (metric === 'drivers') {
      const count = activeDimension?.paretoCount80 ?? 0;
      const itemCount = activeDimension?.itemCount ?? 0;
      const rest = Math.max(0, itemCount - count);
      const rows = items.slice(0, MAX_ROWS);
      const hiddenCount = items.length - rows.length;
      const hiddenValue = items.slice(rows.length).reduce((sum, it) => sum + it.value, 0);
      const dimTotal = activeDimension?.dimensionTotal ?? 0;
      const driversValue = items.slice(0, count).reduce((sum, it) => sum + it.value, 0);
      return (
        <Stack spacing={2}>
          <Paper variant='outlined' sx={{ p: 2.5 }}>
            <Stack direction='row' flexWrap='wrap' useFlexGap sx={{ gap: 3 }}>
              <Stat label={`of ${itemCount} ${itemCount === 1 ? dimLower : dimPlural} make up 80%`} value={`${count}`} />
              <Stat label={`covered by ${count === 1 ? 'that driver' : 'those drivers'}`} value={`${fmtTco2e.format(driversValue)} tCO₂e`} />
            </Stack>
          </Paper>
          <Lead>
            {count > 0 ? (
              <>
                <b>{count} of {itemCount}</b> {itemCount === 1 ? dimLower : dimPlural} account for 80% of your
                footprint. These are your priority targets: acting on them moves the needle most. The other{' '}
                {rest} {rest === 1 ? 'makes up' : 'make up'} the final 20% between them. Fewer drivers means a
                more concentrated footprint that is easier to tackle.
              </>
            ) : (
              <>No {dimLower} emissions to rank for this period.</>
            )}
          </Lead>
          {rows.length > 0 && (
            <Paper variant='outlined' sx={{ p: 2.5 }}>
              <TableContainer>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, pl: 0 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{dimensionLabel}</TableCell>
                      <TableCell align='right' sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>tCO₂e</TableCell>
                      <TableCell align='right' sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Cumulative</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((it, index) => {
                      const isDriver = it.rank <= count;
                      // Drop the "reaches 80% here" divider immediately after the last driver row.
                      const showBoundary = it.rank === count && index < rows.length - 1;
                      return (
                        <React.Fragment key={`${it.rank}-${it.label}`}>
                          <TableRow sx={isDriver ? { bgcolor: 'action.hover' } : undefined}>
                            <TableCell sx={{ pl: 0, color: 'text.secondary' }}>{it.rank}</TableCell>
                            <TableCell>
                              <Stack direction='row' alignItems='center' spacing={1}>
                                <span>{it.label}</span>
                                {isDriver && <Chip size='small' label='Driver' color='primary' variant='outlined' />}
                              </Stack>
                            </TableCell>
                            <TableCell align='right' sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtTco2e.format(it.value)}</TableCell>
                            <TableCell align='right' sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>{fmtPct(it.cumulativePct)}</TableCell>
                          </TableRow>
                          {showBoundary && (
                            <TableRow>
                              <TableCell colSpan={4} sx={{ py: 0.5, border: 0 }}>
                                <Divider textAlign='left'>
                                  <Typography variant='caption' color='primary.main'>80% of footprint reached</Typography>
                                </Divider>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {hiddenCount > 0 && (
                <Typography variant='caption' color='text.secondary' sx={{ mt: 1, display: 'block' }}>
                  + {hiddenCount} more {hiddenCount === 1 ? dimLower : dimPlural} ({fmtTco2e.format(hiddenValue)} tCO₂e,{' '}
                  {fmtPct(dimTotal > 0 ? (hiddenValue / dimTotal) * 100 : 0)} of {dimLower} emissions) not shown.
                </Typography>
              )}
            </Paper>
          )}
        </Stack>
      );
    }

    // biggestMover
    const mover = data.biggestMover;
    if (!mover) {
      return (
        <Typography variant='body2' color='text.secondary' sx={{ py: 4 }}>
          {data.comparison ? 'No contributor increased versus the comparison period.' : 'No comparison period is available, so no movement can be shown.'}
        </Typography>
      );
    }
    const otherMovers: IHotspotItem[] = (data.dimensions?.[data.defaultDimension]?.items ?? [])
      .filter((it) => it.label !== mover.label && it.deltaAbs !== null && (it.deltaAbs ?? 0) > 0)
      .sort((a, b) => (b.deltaAbs ?? 0) - (a.deltaAbs ?? 0))
      .slice(0, 5);
    return (
      <Stack spacing={2}>
        <Paper variant='outlined' sx={{ p: 2.5 }}>
          <Stack direction='row' flexWrap='wrap' useFlexGap sx={{ gap: 3 }}>
            <Stat label='current emissions' value={`${fmtTco2e.format(mover.value)} tCO₂e`} />
            <Stack direction='row' alignItems='center' spacing={1}>
              <DeltaInline deltaAbs={mover.deltaAbs} deltaPct={mover.deltaPct} isNew={mover.isNew} />
              <Typography variant='caption' color='text.secondary'>{priorLabel}</Typography>
            </Stack>
          </Stack>
        </Paper>
        <Lead>
          <b>{mover.label}</b> had the largest increase versus the comparison period, from your {defaultDimensionLabel.toLowerCase()}{' '}
          breakdown. {mover.isNew
            ? 'It is new this period, with no prior-period emissions to compare against.'
            : <>It rose by {fmtTco2e.format(Math.abs(mover.deltaAbs ?? 0))} tCO₂e{mover.deltaPct !== null ? ` (${fmtPct(Math.abs(mover.deltaPct))})` : ''}.</>}
          {' '}The biggest mover is a good place to ask what changed: real growth, a data correction, or a new supplier.
        </Lead>
        {otherMovers.length > 0 && (
          <Paper variant='outlined' sx={{ p: 2.5 }}>
            <Typography variant='subtitle2' sx={{ mb: 1 }}>Other notable increases</Typography>
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, pl: 0 }}>{defaultDimensionLabel}</TableCell>
                    <TableCell align='right' sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{priorLabel}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {otherMovers.map((it) => (
                    <TableRow key={`${it.rank}-${it.label}`}>
                      <TableCell sx={{ pl: 0 }}>{it.label}</TableCell>
                      <TableCell align='right'>
                        <DeltaInline deltaAbs={it.deltaAbs} deltaPct={it.deltaPct} isNew={it.isNew} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Stack>
    );
  };

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          top: { xs: 0, sm: 64 },
          height: { xs: '100%', sm: 'calc(100vh - 64px)' },
          borderTopLeftRadius: { xs: 0, sm: 24 },
          borderBottomLeftRadius: { xs: 0, sm: 24 },
          overflow: 'hidden',
          boxShadow: '-12px 0 32px rgba(17, 30, 33, 0.14)',
          borderLeft: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <Box sx={{ width: { xs: '100vw', sm: 580 }, maxWidth: '100vw', height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#fbfcfe' }}>
        {/* Header */}
        <Stack direction='row' justifyContent='space-between' alignItems='flex-start' sx={{ px: 3, py: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant='caption' color='text.secondary'>
              {metric && (metric === 'total' || metric === 'biggestMover') ? 'Metric' : `By ${dimLower}`}
            </Typography>
            <Typography variant='h6' sx={{ color: 'text.primary', lineHeight: 1.2, wordBreak: 'break-word' }}>
              {metric ? METRIC_TITLE[metric] : ''}
            </Typography>
          </Box>
          <IconButton aria-label='Close' onClick={onClose}><CloseIcon /></IconButton>
        </Stack>

        <Box sx={{ flex: 1, overflowY: 'auto', px: 3, pb: 3 }}>
          {renderBody()}
        </Box>
      </Box>
    </Drawer>
  );
};
